import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Confetti from 'react-confetti';
import { useAuth } from '../contexts/AuthContext';
import { getLesson, saveLesson, addXP } from '../services/firebase';
import { API_URL } from '../config';
import AIAssistant from './AIAssistant';
import VoiceMentor from './VoiceMentor';
import './InteractiveLesson.css';

function InteractiveLesson() {
    const [lesson, setLesson] = useState(null);
    const [currentStep, setCurrentStep] = useState(-1);
    const [loading, setLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingStatus, setLoadingStatus] = useState('Loading...');
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [xpEarned, setXpEarned] = useState(0);
    const [streak, setStreak] = useState(0);
    const [showConfetti, setShowConfetti] = useState(false);
    const [showXPPopup, setShowXPPopup] = useState(false);
    const [lastXPGain, setLastXPGain] = useState(0);
    const [showVoiceMentor, setShowVoiceMentor] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, refreshUserData } = useAuth();

    // Get lesson params from state or localStorage (for refresh support)
    const stateParams = location.state || {};
    const [lessonParams] = useState(() => {
        if (stateParams.moduleId && stateParams.topic) {
            // Save to localStorage for refresh support
            localStorage.setItem('currentLessonParams', JSON.stringify(stateParams));
            return stateParams;
        }
        // Try to recover from localStorage
        try {
            const saved = localStorage.getItem('currentLessonParams');
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });

    const { moduleId, topic, moduleTitle, moduleDescription, difficulty, fromWorldMap } = lessonParams;

    useEffect(() => {
        if (!moduleId || !topic) {
            navigate('/home');
            return;
        }
        loadLesson();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleId, topic]);

    const loadLesson = async () => {
        setLoading(true);
        setLoadingProgress(0);
        setLoadingStatus('Starting...');

        try {
            if (!currentUser) {
                navigate('/auth');
                return;
            }

            setLoadingProgress(5);
            setLoadingStatus('Checking for saved lesson...');

            // Try to load from Firebase first
            const cachedResult = await getLesson(currentUser.uid, topic, moduleId);

            if (cachedResult.success) {
                const cached = cachedResult.data;
                // Validate cached lesson has required content with actual data
                const hasIntro = cached.intro?.text && cached.intro.text.length > 50;
                const hasTeaching = cached.teachingContent?.length > 0 &&
                                    cached.teachingContent[0]?.content?.length > 50;
                const hasQuestions = cached.interactiveElements?.length > 0 &&
                                     cached.interactiveElements[0]?.question?.length > 10;
                const isValidLesson = hasIntro && hasTeaching && hasQuestions;

                if (isValidLesson) {
                    console.log('📦 Loading cached lesson from Firebase');
                    setLoadingProgress(100);
                    setLesson(cached);
                    setLoading(false);
                    return;
                } else {
                    console.log('⚠️ Cached lesson is incomplete or empty, regenerating...');
                    console.log('Cache validation:', { hasIntro, hasTeaching, hasQuestions });
                }
            }

            setLoadingProgress(15);
            setLoadingStatus('Checking for pre-generated content...');

            // Try to get pre-generated lesson from background generation
            try {
                console.log('🚀 Checking for pre-generated lesson...');
                const preGenResponse = await fetch(
                    `${API_URL}/api/generation/content/${currentUser.uid}/${encodeURIComponent(topic)}`
                );

                if (preGenResponse.ok) {
                    const preGenData = await preGenResponse.json();
                    if (preGenData.success && preGenData.lessons && preGenData.lessons[moduleId]) {
                        console.log('✨ Using pre-generated lesson!');
                        setLoadingProgress(90);
                        setLoadingStatus('Loading pre-generated lesson...');
                        const lessonData = preGenData.lessons[moduleId];

                        // Save to Firebase for future loads
                        await saveLesson(currentUser.uid, topic, moduleId, lessonData);
                        setLoadingProgress(100);
                        setLesson(lessonData);
                        setLoading(false);
                        return;
                    }
                }
            } catch (preGenError) {
                console.log('Pre-generated lesson not available');
            }

            // Generate new lesson
            console.log('🤖 Generating new lesson...');
            setLoadingProgress(20);
            setLoadingStatus('AI is creating your lesson...');

            // Start progress simulation while waiting for AI
            const progressInterval = setInterval(() => {
                setLoadingProgress(prev => {
                    if (prev < 85) return prev + Math.random() * 3;
                    return prev;
                });
            }, 500);

            const response = await fetch(`${API_URL}/api/generate-lesson`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    topic,
                    moduleId,
                    moduleTitle,
                    moduleDescription,
                    difficulty: difficulty || 'intermediate'
                })
            });

            // Clear the progress simulation
            clearInterval(progressInterval);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Server error: ${response.status}`);
            }

            setLoadingProgress(90);
            setLoadingStatus('Processing lesson content...');

            const lessonData = await response.json();

            // Check if lesson data is valid
            if (!lessonData || !lessonData.title) {
                throw new Error('Invalid lesson data received from server');
            }

            setLoadingProgress(95);
            setLoadingStatus('Saving to your account...');

            // Save to Firebase
            console.log('🔄 Attempting to save lesson...');
            const saveResult = await saveLesson(currentUser.uid, topic, moduleId, lessonData);

            if (!saveResult.success) {
                console.error('⚠️ Failed to save lesson to Firebase:', saveResult.error);
                alert(`Warning: Lesson generated but not saved to database. Error: ${saveResult.error}`);
            } else {
                console.log('✅ Lesson cached to Firebase!');
            }

            setLoadingProgress(100);
            setLesson(lessonData);

            // Pre-fetch next lesson in background for faster loading
            prefetchNextLesson(moduleId + 1);
        } catch (error) {
            console.error('Error loading lesson:', error);
            alert(`Failed to load lesson: ${error.message}. Please try again.`);
            navigate('/learning-path', { state: { topic } });
        }
        setLoading(false);
    };

    // Pre-fetch the next lesson in background for instant loading
    const prefetchNextLesson = async (nextModuleId) => {
        if (!currentUser || !topic) return;

        try {
            console.log(`🚀 Pre-fetching lesson ${nextModuleId} in background...`);

            // Check if already cached
            const cachedResult = await getLesson(currentUser.uid, topic, nextModuleId);
            if (cachedResult.success) {
                console.log(`✅ Lesson ${nextModuleId} already cached`);
                return;
            }

            // Generate in background (don't await - fire and forget)
            fetch(`${API_URL}/api/generate-lesson`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    topic,
                    moduleId: nextModuleId,
                    moduleTitle: `Module ${nextModuleId}`, // Will be overwritten by actual content
                    difficulty: difficulty || 'intermediate'
                })
            }).then(async (response) => {
                if (response.ok) {
                    const lessonData = await response.json();
                    if (lessonData && lessonData.title) {
                        await saveLesson(currentUser.uid, topic, nextModuleId, lessonData);
                        console.log(`✅ Pre-fetched and cached lesson ${nextModuleId}`);
                    }
                }
            }).catch(err => {
                console.log(`Pre-fetch for lesson ${nextModuleId} failed (non-critical):`, err.message);
            });
        } catch (error) {
            // Non-critical error - just log it
            console.log('Pre-fetch error (non-critical):', error.message);
        }
    };

    const saveProgress = async (xp) => {
        if (!currentUser) return;

        try {
            // Add XP to user's total
            await addXP(currentUser.uid, xp, 'lesson');

            // Refresh user data to show updated XP
            await refreshUserData();
        } catch (error) {
            console.error('Error saving progress:', error);
        }
    };

    const handleAnswerSelect = async (answer) => {
        if (showFeedback) return;

        setSelectedAnswer(answer);
        setShowFeedback(true);

        const currentElement = currentStep < lesson.interactiveElements.length
            ? lesson.interactiveElements[currentStep]
            : lesson.practiceChallenge;

        const isCorrect = answer === currentElement.correctAnswer;

        if (isCorrect) {
            const earnedXP = currentElement.xp || 30;
            setXpEarned(xpEarned + earnedXP);
            setStreak(streak + 1);
            setLastXPGain(earnedXP);
            
            // Visual feedback
            setShowConfetti(true);
            setShowXPPopup(true);
            setTimeout(() => setShowConfetti(false), 3000);
            setTimeout(() => setShowXPPopup(false), 2000);

            await saveProgress(earnedXP);
        } else {
            setStreak(0);
        }
    };

    // Extract lesson content for AI Mentor
    const getLessonContentForMentor = () => {
        if (!lesson) return '';

        let content = `LESSON: ${lesson.title}\n\n`;

        // Add intro
        if (lesson.intro?.text) {
            content += `INTRODUCTION:\n${lesson.intro.text}\n\n`;
        }

        // Add key points
        if (lesson.intro?.keyPoints?.length > 0) {
            content += `KEY POINTS:\n${lesson.intro.keyPoints.map(p => `- ${p}`).join('\n')}\n\n`;
        }

        // Add teaching content
        if (lesson.teachingContent?.length > 0) {
            lesson.teachingContent.forEach(section => {
                content += `${section.section}:\n${section.content}\n`;
                if (section.example) {
                    content += `Example: ${section.example}\n`;
                }
                content += '\n';
            });
        }

        return content;
    };

    const handleNextStep = () => {
        setShowFeedback(false);
        setSelectedAnswer(null);

        const totalSteps = lesson.interactiveElements.length;
        
        if (currentStep < totalSteps - 1) {
            setCurrentStep(currentStep + 1);
        } else if (currentStep === totalSteps - 1) {
            // Move to practice challenge
            setCurrentStep(totalSteps);
        } else {
            // Lesson complete - pass lesson content for flashcard generation
            navigate('/lesson-complete', {
                state: {
                    xpEarned: xpEarned,
                    moduleId: moduleId,
                    topic: topic,
                    maxStreak: streak,
                    fromWorldMap: fromWorldMap,
                    lessonTitle: lesson.title,
                    lessonContent: lesson.teachingContent,
                    lessonQuestions: lesson.interactiveElements
                }
            });
        }
    };

    const renderIntro = () => (
        <div className="lesson-intro">
            <div className="lesson-header-section">
                <h1 className="lesson-title">{lesson.title || 'Lesson'}</h1>
                <p className="lesson-duration">⏱️ {lesson.duration || '15 min'}</p>
            </div>

            <div className="intro-content">
                <div className="intro-text">
                    {(lesson.intro?.text || '').split('\n\n').map((paragraph, idx) => (
                        paragraph.trim() && <p key={idx} className="intro-paragraph">{paragraph}</p>
                    ))}
                </div>

                {lesson.intro?.keyPoints && lesson.intro.keyPoints.length > 0 && (
                <div className="key-points-section">
                    <h3>🎯 Key Takeaways</h3>
                    <div className="key-points-grid">
                        {lesson.intro.keyPoints.map((point, idx) => (
                            <div key={idx} className="key-point-card">
                                <span className="key-point-icon">✓</span>
                                <span className="key-point-text">{point}</span>
                            </div>
                        ))}
                    </div>
                </div>
                )}

                {/* Teaching Content */}
                {lesson.teachingContent && lesson.teachingContent.map((section, idx) => {
                    // Parse memory tricks from content
                    const contentLines = (section.content || '').split('\n');
                    const memoryTricks = [];
                    const regularContent = [];

                    contentLines.forEach(line => {
                        if (line.includes('💡 MEMORY TRICK:') || line.includes('MEMORY TRICK:')) {
                            memoryTricks.push(line.replace('💡 MEMORY TRICK:', '').replace('MEMORY TRICK:', '').trim());
                        } else if (line.trim()) {
                            regularContent.push(line);
                        }
                    });

                    return (
                        <div key={idx} className="teaching-section">
                            <h2 className="section-title">{section.section}</h2>
                            <div className="section-content">
                                {regularContent.join('\n\n').split('\n\n').map((paragraph, pIdx) => {
                                    if (!paragraph.trim()) return null;
                                    return <p key={pIdx} className="content-paragraph">{paragraph}</p>;
                                })}
                            </div>

                            {/* Memory Tricks - Special Display */}
                            {memoryTricks.length > 0 && memoryTricks.map((trick, tIdx) => (
                                <div key={tIdx} className="memory-trick-box">
                                    <div className="memory-trick-header">
                                        <span className="memory-trick-icon">💡</span>
                                        <strong>Memory Trick</strong>
                                    </div>
                                    <p className="memory-trick-text">{trick}</p>
                                </div>
                            ))}

                            {section.example && (
                                <div className="example-box">
                                    <div className="example-header">
                                        <span className="example-icon">🎯</span>
                                        <strong>Real-World Example</strong>
                                    </div>
                                    <p className="example-text">{section.example}</p>
                                </div>
                            )}
                        </div>
                    );
                })}

                <div className="lesson-action-buttons">
                    <button className="btn btn-primary btn-large start-practice-btn" onClick={() => setCurrentStep(0)}>
                        🚀 Start Practice Questions
                    </button>
                    <button
                        className="btn btn-mentor mentor-mode-btn"
                        onClick={() => setShowVoiceMentor(true)}
                    >
                        🎓 AI Mentor Mode
                    </button>
                </div>
            </div>
        </div>
    );

    const renderQuestion = () => {
        const interactiveElements = lesson.interactiveElements || [];
        const isChallenge = currentStep >= interactiveElements.length;
        const element = isChallenge ? lesson.practiceChallenge : interactiveElements[currentStep];
        const progress = ((currentStep + 1) / (interactiveElements.length + 1)) * 100;

        // Safety check - if no element, go back to intro
        if (!element) {
            return (
                <div className="question-section">
                    <p>No questions available.</p>
                    <button className="btn btn-primary" onClick={() => setCurrentStep(-1)}>
                        Back to Lesson
                    </button>
                </div>
            );
        }

        return (
            <div className="question-section">
                <div className="question-progress-bar">
                    <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>

                <div className="question-header">
                    <span className="question-number">
                        {isChallenge ? '🏆 Final Challenge' : `Question ${currentStep + 1} of ${interactiveElements.length}`}
                    </span>
                </div>

                {isChallenge && element.title && (
                    <div className="challenge-banner">
                        <h3>{element.title}</h3>
                        <p>{element.scenario || ''}</p>
                    </div>
                )}

                <div className="question-card">
                    <h2 className="question-text">{element.question || 'Question'}</h2>

                    <div className="options-container">
                        {(element.options || []).map((option, idx) => (
                            <button
                                key={idx}
                                className={`option-btn ${
                                    showFeedback && option === element.correctAnswer ? 'correct' :
                                    showFeedback && option === selectedAnswer ? 'incorrect' : ''
                                }`}
                                onClick={() => handleAnswerSelect(option)}
                                disabled={showFeedback}
                            >
                                <span className="option-letter">{String.fromCharCode(65 + idx)}</span>
                                <span className="option-text">{option}</span>
                            </button>
                        ))}
                    </div>

                    {showFeedback && (
                        <div className={`feedback-card ${selectedAnswer === element.correctAnswer ? 'correct-feedback' : 'incorrect-feedback'}`}>
                            <div className="feedback-header">
                                <span className="feedback-icon">
                                    {selectedAnswer === element.correctAnswer ? '✅' : '❌'}
                                </span>
                                <h3>{selectedAnswer === element.correctAnswer ? 'Correct!' : 'Not Quite'}</h3>
                            </div>
                            <p className="feedback-text">
                                {selectedAnswer === element.correctAnswer 
                                    ? element.feedback?.correct || element.explanation
                                    : element.feedback?.incorrect || element.explanation}
                            </p>
                            {selectedAnswer === element.correctAnswer && (
                                <div className="xp-earned-display">
                                    <span className="xp-icon">💎</span>
                                    <span>+{element.xp} XP</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {showFeedback && (
                    <button className="btn btn-primary btn-large continue-btn" onClick={handleNextStep}>
                        {isChallenge ? '🎉 Complete Lesson' : 'Continue →'}
                    </button>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="loading-container" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: '2rem',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
            }}>
                <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '20px',
                    padding: '3rem',
                    maxWidth: '500px',
                    width: '100%',
                    textAlign: 'center'
                }}>
                    <div className="spinner" style={{ margin: '0 auto 1.5rem' }}></div>
                    <h2 style={{ color: '#fff', marginBottom: '0.5rem', fontSize: '1.5rem' }}>
                        {moduleTitle || 'Loading Lesson'}
                    </h2>
                    <p style={{ color: '#a0aec0', marginBottom: '2rem', fontSize: '0.9rem' }}>
                        {topic}
                    </p>

                    {/* Progress Bar */}
                    <div style={{
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        height: '12px',
                        overflow: 'hidden',
                        marginBottom: '1rem'
                    }}>
                        <div style={{
                            background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                            height: '100%',
                            width: `${loadingProgress}%`,
                            borderRadius: '10px',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: '#a0aec0',
                        fontSize: '0.9rem'
                    }}>
                        <span>{loadingStatus}</span>
                        <span style={{ fontWeight: 'bold', color: '#10b981' }}>
                            {Math.round(loadingProgress)}%
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    if (!lesson) {
        return (
            <div className="error-container">
                <h2>😕 Oops!</h2>
                <p>No lesson content available</p>
                <p style={{fontSize: '0.9rem', opacity: 0.7}}>Topic: {topic || 'Unknown'}</p>
                <button className="btn btn-primary" onClick={() => navigate('/learning-path', { state: { topic } })}>
                    Go Back
                </button>
                <button
                    className="btn btn-secondary"
                    style={{marginTop: '0.5rem'}}
                    onClick={() => {
                        setLoading(true);
                        loadLesson();
                    }}
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="interactive-lesson">
            {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}

            {/* Header */}
            <div className="lesson-top-bar">
                <button className="back-button" onClick={() => navigate('/learning-path')}>
                    ← Back to Path
                </button>
                <div className="stats-bar">
                    <div className="stat-badge xp-badge">
                        <span className="badge-icon">💎</span>
                        <span className="badge-value">{xpEarned}</span>
                    </div>
                    <div className="stat-badge streak-badge">
                        <span className="badge-icon">🔥</span>
                        <span className="badge-value">{streak}</span>
                    </div>
                </div>
            </div>

            {/* XP Popup */}
            {showXPPopup && (
                <div className="xp-popup">
                    <span className="xp-popup-icon">💎</span>
                    <span className="xp-popup-text">+{lastXPGain} XP</span>
                </div>
            )}

            {/* Content */}
            <div className="lesson-content">
                {currentStep === -1 ? renderIntro() : renderQuestion()}
            </div>

            {/* AI Assistant - only show during actual lesson steps */}
            {currentStep >= 0 && lesson && (
                <AIAssistant
                    lessonContext={{
                        topic,
                        moduleId,
                        currentStep: currentStep + 1,
                        totalSteps: lesson.questions?.length || 0,
                        currentQuestion: lesson.questions?.[currentStep]
                    }}
                />
            )}

            {/* Voice Mentor Mode */}
            {showVoiceMentor && lesson && (
                <VoiceMentor
                    lessonContent={getLessonContentForMentor()}
                    lessonTitle={lesson.title}
                    onClose={() => setShowVoiceMentor(false)}
                />
            )}
        </div>
    );
}

export default InteractiveLesson;